import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Copy, Pencil } from "lucide-react";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type KeyboardEvent,
} from "react";
import { toast } from "sonner";

import { getApiErrorMessage, IApiAxiosError } from "@/api/api.types";
import type {
    IExtractedCOIResponse,
    IExtractedCOIUpdate,
} from "@/api/documents";
import { updateDocumentCoi } from "@/api/documents";
import { Button } from "@/components/ui/button";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { copyToClipboardWithToast } from "@/lib/clipboard";
import { cn } from "@/lib/utils";
import { getPropertyDisplayValue, isEmptyPropertyValue } from "@/utils/fields";

const INSURER_LETTERS = ["a", "b", "c", "d", "e", "f"] as const;
type InsurerLetter = (typeof INSURER_LETTERS)[number];

interface CoverageOption {
    name: string;
    value?: boolean | string;
}

interface CoverageLimit {
    name: string;
    value?: string | boolean;
}

interface Coverage {
    type: string;
    insurer_letter?: string;
    coverage_options?: CoverageOption[];
    additional_insured?: boolean;
    subrogation_waived?: boolean;
    policy_number?: string;
    policy_effective_date?: string;
    policy_expiration_date?: string;
    limits?: CoverageLimit[];
}

function isCoverage(v: unknown): v is Coverage {
    return typeof v === "object" && v !== null && "type" in v;
}

interface ExtractedCOISectionProps {
    documentId: number;
    coi: IExtractedCOIResponse;
    /** When false, fields whose value is empty/null are hidden. */
    showEmpty?: boolean;
    /**
     * When supplied, the "Certificate Details" page pill becomes a button
     * that bubbles the COI page up to `DocumentDetailPanel`'s `pdfModal`
     * state. `propertyId` is `null` because the COI is a doc-level record
     * (no per-property highlight to overlay).
     */
    onOpenInDocument?: (payload: {
        documentId: number;
        propertyId: number | null;
        pageNumber: number;
        fieldName: string;
        source: string | null;
    }) => void;
}

// ── Section wrapper ─────────────────────────────────────────────────────────

function COISection({
    title,
    pageNumber,
    onPageClick,
    children,
}: {
    title: string;
    pageNumber?: number | null;
    /**
     * When provided alongside `pageNumber`, the page pill becomes a button
     * that opens the source PDF at that page. Visual styling stays identical
     * to the static pill so the section header doesn't gain weight.
     */
    onPageClick?: () => void;
    children: React.ReactNode;
}) {
    const pillClasses = cn(
        "border-border bg-muted text-muted-foreground inline-flex h-5 shrink-0 items-center rounded-full border px-1.5",
        "font-mono text-[10px] leading-none font-medium"
    );
    return (
        <div className="space-y-1">
            <div className="flex items-center gap-2">
                <h4 className="text-foreground text-sm font-semibold">
                    {title}
                </h4>
                {pageNumber != null &&
                    (onPageClick ? (
                        <button
                            type="button"
                            onClick={onPageClick}
                            aria-label={`Open document at page ${pageNumber}`}
                            className={cn(
                                pillClasses,
                                "hover:bg-accent hover:text-foreground focus-visible:ring-ring/40 cursor-pointer transition-colors outline-none focus-visible:ring-2"
                            )}
                        >
                            p.{pageNumber}
                        </button>
                    ) : (
                        <span className={pillClasses}>p.{pageNumber}</span>
                    ))}
            </div>
            <div className="border-border divide-border divide-y rounded-lg border">
                {children}
            </div>
        </div>
    );
}

// ── COIFieldRow (compact 2-col, label-left, value-right) ───────────────────
//
// Design (screenshot 3) renders COI rows as `label | value-right-aligned`
// with no per-row chrome (no pencil, no copy, no trash, no page badge). The
// canonical `ExtractedFieldRow` has heavier 3-col chrome that doesn't fit
// the COI surface, so this row is deliberately separate. Click-to-edit on
// the value is preserved when `onSave` is provided.
function COIFieldRow({
    label,
    value,
    onSave,
}: {
    label: string;
    value: string;
    onSave?: (next: string | null) => void | Promise<void>;
}) {
    const editable = !!onSave;
    const [mode, setMode] = useState<"read" | "edit">("read");
    const [draft, setDraft] = useState(value ?? "");
    const inputRef = useRef<HTMLInputElement>(null);
    const rowRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (mode === "read") setDraft(value ?? "");
    }, [value, mode]);

    useEffect(() => {
        if (mode === "edit") {
            requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            });
        }
    }, [mode]);

    const commit = useCallback(async () => {
        const trimmed = draft.trim();
        const normalized = trimmed === "" ? null : trimmed;
        const original = value === "" ? null : value;
        if (normalized !== original) {
            try {
                await onSave?.(normalized);
            } catch {
                // Mutation onError already toasts. Returning to read mode
                // lets the row sync with the rolled-back cache value instead
                // of stranding the user's failed input on screen.
            }
        }
        setMode("read");
    }, [draft, value, onSave]);

    const cancel = useCallback(() => {
        setDraft(value ?? "");
        setMode("read");
    }, [value]);

    // Native Esc listener on the row so it can stopPropagation BEFORE the
    // panel's native Esc listener fires. Mirrors the pattern in
    // ExtractedFieldRow, where a synthetic-only Esc handler dispatches after
    // the native bubble has already reached the panel root.
    useEffect(() => {
        if (mode !== "edit") return;
        const node = rowRef.current;
        if (!node) return;
        const handler = (e: globalThis.KeyboardEvent) => {
            if (e.key !== "Escape") return;
            e.stopPropagation();
            cancel();
        };
        node.addEventListener("keydown", handler);
        return () => node.removeEventListener("keydown", handler);
    }, [mode, cancel]);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            commit();
        }
    };

    const isEmpty = isEmptyPropertyValue(value);

    const handleCopy = useCallback(() => {
        if (!value) return;
        void copyToClipboardWithToast(value);
    }, [value]);

    if (mode === "edit") {
        return (
            <div
                ref={rowRef}
                role="row"
                aria-label={`Editing ${label}`}
                className="grid grid-cols-[minmax(120px,40%)_1fr_auto] items-center gap-3 px-3 py-2.5"
            >
                <span className="text-muted-foreground truncate text-sm">
                    {label}
                </span>
                <Input
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={commit}
                    aria-label={`Value for ${label}`}
                    className="h-7 text-right text-sm"
                />
                <span aria-hidden className="w-12" />
            </div>
        );
    }

    return (
        <div
            role="row"
            aria-label={label}
            className="group/row grid grid-cols-[minmax(120px,40%)_1fr_auto] items-start gap-3 px-3 py-2.5"
        >
            <span className="text-muted-foreground truncate text-sm">
                {label}
            </span>
            {editable ? (
                <button
                    type="button"
                    onClick={() => setMode("edit")}
                    className={cn(
                        "block w-full rounded text-right text-sm leading-snug break-words",
                        "hover:bg-accent/40 focus-visible:ring-ring/40 outline-none focus-visible:ring-2",
                        isEmpty ? "text-muted-foreground/50" : "text-foreground"
                    )}
                >
                    {isEmpty ? "—" : value}
                </button>
            ) : (
                <span
                    className={cn(
                        "block text-right text-sm leading-snug break-words",
                        isEmpty ? "text-muted-foreground/50" : "text-foreground"
                    )}
                >
                    {isEmpty ? "—" : value}
                </span>
            )}
            {editable ? (
                <div className="flex items-start gap-0.5">
                    <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={handleCopy}
                        aria-label="Copy value"
                        className="opacity-0 transition-opacity duration-150 group-focus-within/row:opacity-100 group-hover/row:opacity-100"
                    >
                        <Copy />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => setMode("edit")}
                        aria-label={`Edit ${label}`}
                    >
                        <Pencil />
                    </Button>
                </div>
            ) : (
                <span aria-hidden />
            )}
        </div>
    );
}

// ── Insurer row ─────────────────────────────────────────────────────────────
//
// Insurer rows use a 2-column grid:
//
//     [A–F chip] [stacked name / NAIC (1fr)]
//
// Name and NAIC are stacked vertically inside the right column so a long
// carrier name doesn't truncate the NAIC. Each value is its own
// `EditableTextLine` with independent click-to-edit / copy affordances; the
// per-row page badge that earlier drafts carried was dropped in favor of
// the per-section badge rendered by `COISection`.
//
// Do NOT "DRY this up" by reusing `ExtractedFieldRow`; the local
// `COIFieldRow` and `EditableTextLine` deliberately omit the heavier chrome
// (label column, per-row page badge) that the canonical row carries.

// Single-value click-to-edit line with hover-revealed copy + edit buttons.
// Used for stacked name/NAIC values inside InsurerRow where each value gets
// independent edit/copy affordances. The COIFieldRow next to it has a label
// column; this one doesn't (caller-controlled visual rendering only).
function EditableTextLine({
    label,
    value,
    onSave,
    valueClassName,
    placeholder = "—",
}: {
    label: string;
    value: string;
    onSave?: (next: string | null) => void | Promise<void>;
    valueClassName?: string;
    placeholder?: string;
}) {
    const editable = !!onSave;
    const [mode, setMode] = useState<"read" | "edit">("read");
    const [draft, setDraft] = useState(value ?? "");
    const inputRef = useRef<HTMLInputElement>(null);
    const rowRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (mode === "read") setDraft(value ?? "");
    }, [value, mode]);

    useEffect(() => {
        if (mode === "edit") {
            requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            });
        }
    }, [mode]);

    const commit = useCallback(async () => {
        const trimmed = draft.trim();
        const normalized = trimmed === "" ? null : trimmed;
        const original = value === "" ? null : value;
        if (normalized !== original) {
            try {
                await onSave?.(normalized);
            } catch {
                // See COIFieldRow.commit; error is surfaced via the
                // mutation's toast and the row resets so it doesn't strand
                // the user's failed input.
            }
        }
        setMode("read");
    }, [draft, value, onSave]);

    const cancel = useCallback(() => {
        setDraft(value ?? "");
        setMode("read");
    }, [value]);

    useEffect(() => {
        if (mode !== "edit") return;
        const node = rowRef.current;
        if (!node) return;
        const handler = (e: globalThis.KeyboardEvent) => {
            if (e.key !== "Escape") return;
            e.stopPropagation();
            cancel();
        };
        node.addEventListener("keydown", handler);
        return () => node.removeEventListener("keydown", handler);
    }, [mode, cancel]);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            commit();
        }
    };

    const isEmpty = isEmptyPropertyValue(value);

    const handleCopy = useCallback(() => {
        if (!value) return;
        void copyToClipboardWithToast(value);
    }, [value]);

    if (mode === "edit") {
        return (
            <div ref={rowRef} className="flex items-center gap-1">
                <Input
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={commit}
                    aria-label={label}
                    className={cn("h-7 flex-1 text-sm", valueClassName)}
                />
            </div>
        );
    }

    return (
        <div className="group/inline flex items-center gap-1">
            {editable ? (
                <button
                    type="button"
                    onClick={() => setMode("edit")}
                    aria-label={`Edit ${label}`}
                    className={cn(
                        "block min-w-0 flex-1 truncate rounded text-left text-sm leading-snug",
                        "hover:bg-accent/40 focus-visible:ring-ring/40 outline-none focus-visible:ring-2",
                        isEmpty
                            ? "text-muted-foreground/50"
                            : "text-foreground",
                        valueClassName
                    )}
                >
                    {isEmpty ? placeholder : value}
                </button>
            ) : (
                <span
                    className={cn(
                        "block min-w-0 flex-1 truncate text-left text-sm leading-snug",
                        isEmpty
                            ? "text-muted-foreground/50"
                            : "text-foreground",
                        valueClassName
                    )}
                >
                    {isEmpty ? placeholder : value}
                </span>
            )}
            {editable && (
                <div className="ml-auto flex shrink-0 items-center gap-0.5">
                    <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={handleCopy}
                        aria-label={`Copy ${label}`}
                        className="opacity-0 transition-opacity duration-150 group-focus-within/inline:opacity-100 group-hover/inline:opacity-100"
                    >
                        <Copy />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => setMode("edit")}
                        aria-label={`Edit ${label}`}
                    >
                        <Pencil />
                    </Button>
                </div>
            )}
        </div>
    );
}

function InsurerRow({
    letter,
    name,
    naic,
    onSaveName,
    onSaveNaic,
}: {
    letter: InsurerLetter;
    name: string | null;
    naic: string | null;
    onSaveName?: (next: string | null) => void | Promise<void>;
    onSaveNaic?: (next: string | null) => void | Promise<void>;
}) {
    const upper = letter.toUpperCase();
    return (
        <div
            data-testid="insurer-row"
            className="grid items-start gap-3 px-3 py-2.5"
            style={{ gridTemplateColumns: "20px 1fr" }}
        >
            <span
                data-testid="insurer-letter-chip"
                className="bg-muted text-foreground mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-sm text-xs font-medium"
            >
                {upper}
            </span>
            <div className="flex min-w-0 flex-col gap-2.5">
                <EditableTextLine
                    label={`Insurer ${upper} name`}
                    value={name ?? ""}
                    onSave={onSaveName}
                    placeholder="Not specified"
                />
                <EditableTextLine
                    label={`Insurer ${upper} NAIC`}
                    value={naic ?? ""}
                    onSave={onSaveNaic}
                    placeholder="No NAIC"
                    valueClassName="font-mono text-xs tabular-nums text-muted-foreground"
                />
            </div>
        </div>
    );
}

// ── Coverage card ───────────────────────────────────────────────────────────
//
// Reuses the chevron + label + count + body collapsible pattern established
// in Slice 9 (Plan Unit 8) for sticky group headers. Coverage rows inside an
// expanded card use the canonical `ExtractedFieldRow` (Slice 6 / Plan Unit 4)
// with `readOnly` so layout stays consistent with the rest of the panel.

function CoverageSubGroup({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-1">
            <span className="text-muted-foreground block text-xs font-medium tracking-wide uppercase">
                {label}
            </span>
            <div className="divide-border divide-y">{children}</div>
        </div>
    );
}

function CoverageRow({
    coverage,
    defaultOpen,
}: {
    coverage: Coverage;
    defaultOpen: boolean;
}) {
    const hasPolicyInfo = !!(
        coverage.policy_number ||
        coverage.policy_effective_date ||
        coverage.policy_expiration_date
    );
    const hasOptions = !!coverage.coverage_options?.length;
    const hasDetails =
        coverage.additional_insured !== undefined ||
        coverage.subrogation_waived !== undefined;
    const hasLimits = !!coverage.limits?.length;

    return (
        <Collapsible defaultOpen={defaultOpen}>
            <div className="border-border overflow-hidden rounded-lg border">
                <CollapsibleTrigger className="group bg-muted/40 data-[state=open]:border-border hover:bg-muted/60 flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors data-[state=open]:border-b">
                    <ChevronRight className="text-muted-foreground size-3.5 shrink-0 transition-transform duration-200 group-aria-expanded:rotate-90" />
                    <span className="text-foreground text-sm font-semibold">
                        {coverage.type}
                    </span>
                    {coverage.insurer_letter && (
                        <span className="text-muted-foreground bg-muted ml-auto rounded px-1.5 py-0.5 text-xs">
                            Insurer {coverage.insurer_letter}
                        </span>
                    )}
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="space-y-3 px-3 py-3">
                        {hasPolicyInfo && (
                            <CoverageSubGroup label="Policy Information">
                                {coverage.policy_number !== undefined && (
                                    <COIFieldRow
                                        label="Policy Number"
                                        value={getPropertyDisplayValue(
                                            coverage.policy_number
                                        )}
                                    />
                                )}
                                {coverage.policy_effective_date !==
                                    undefined && (
                                    <COIFieldRow
                                        label="Effective Date"
                                        value={getPropertyDisplayValue(
                                            coverage.policy_effective_date
                                        )}
                                    />
                                )}
                                {coverage.policy_expiration_date !==
                                    undefined && (
                                    <COIFieldRow
                                        label="Expiration Date"
                                        value={getPropertyDisplayValue(
                                            coverage.policy_expiration_date
                                        )}
                                    />
                                )}
                            </CoverageSubGroup>
                        )}

                        {hasOptions && (
                            <CoverageSubGroup label="Coverage Options">
                                {coverage.coverage_options!.map((opt, i) => (
                                    <COIFieldRow
                                        key={i}
                                        label={opt.name}
                                        value={getPropertyDisplayValue(
                                            opt.value
                                        )}
                                    />
                                ))}
                            </CoverageSubGroup>
                        )}

                        {hasDetails && (
                            <CoverageSubGroup label="Coverage Details">
                                {coverage.additional_insured !== undefined && (
                                    <COIFieldRow
                                        label="Additional Insured"
                                        value={getPropertyDisplayValue(
                                            coverage.additional_insured
                                        )}
                                    />
                                )}
                                {coverage.subrogation_waived !== undefined && (
                                    <COIFieldRow
                                        label="Subrogation Waived"
                                        value={getPropertyDisplayValue(
                                            coverage.subrogation_waived
                                        )}
                                    />
                                )}
                            </CoverageSubGroup>
                        )}

                        {hasLimits && (
                            <CoverageSubGroup label="Limits">
                                {coverage.limits!.map((limit, i) => (
                                    <COIFieldRow
                                        key={i}
                                        label={limit.name}
                                        value={getPropertyDisplayValue(
                                            limit.value
                                        )}
                                    />
                                ))}
                            </CoverageSubGroup>
                        )}
                    </div>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
}

function CoveragesBlock({ coverages }: { coverages: Coverage[] }) {
    return (
        <Collapsible defaultOpen>
            <CollapsibleTrigger className="group flex w-full items-center gap-1.5 py-1 text-left">
                <ChevronRight className="text-muted-foreground size-3.5 shrink-0 transition-transform duration-200 group-aria-expanded:rotate-90" />
                <h4 className="text-foreground text-sm font-semibold">
                    Coverages
                </h4>
                <span className="text-muted-foreground text-xs font-normal">
                    {coverages.length}
                </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="mt-2 space-y-2">
                    {coverages.length > 0 ? (
                        coverages.map((coverage, index) => (
                            <CoverageRow
                                key={index}
                                coverage={coverage}
                                // First coverage opens by default so the user
                                // sees a populated body without an extra click.
                                // Subsequent coverages stay collapsed.
                                defaultOpen={index === 0}
                            />
                        ))
                    ) : (
                        <p className="text-muted-foreground py-1 text-sm italic">
                            No coverages found
                        </p>
                    )}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

// ── Section component ──────────────────────────────────────────────────────

type COIFieldKey = keyof IExtractedCOIUpdate;

export function ExtractedCOISection({
    documentId,
    coi,
    showEmpty = false,
    onOpenInDocument,
}: ExtractedCOISectionProps) {
    const queryClient = useQueryClient();

    // Per-field optimistic mutation. Each ExtractedFieldRow's onSave routes
    // through `mutateAsync` here so the host (this section) owns the cache
    // write and rollback. The row's internal mutation is short-circuited via
    // its `onSave` adapter (see ExtractedFieldRow.tsx).
    //
    // Optimistic-update behavior preserved from the pre-redesign component:
    // onMutate snapshots + writes into the ["frontend-document-coi", id]
    // cache, onError rolls back from the snapshot, and onSettled invalidates
    // to reconcile with the server. Slice 11.I migrated the visual layer
    // without touching this contract.
    const updateMutation = useMutation({
        mutationFn: (payload: IExtractedCOIUpdate) =>
            updateDocumentCoi(documentId, payload),
        onMutate: async (payload) => {
            const queryKey = ["frontend-document-coi", documentId];
            await queryClient.cancelQueries({ queryKey });
            const previous =
                queryClient.getQueryData<IExtractedCOIResponse | null>(
                    queryKey
                );
            if (previous) {
                queryClient.setQueryData(queryKey, {
                    ...previous,
                    ...payload,
                });
            }
            return { previous };
        },
        onError: (err: IApiAxiosError, _payload, context) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData(
                    ["frontend-document-coi", documentId],
                    context.previous
                );
            }
            toast.error(
                getApiErrorMessage(err, "Failed to update certificate")
            );
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-document-coi", documentId],
            });
        },
    });

    function saveField(fieldKey: COIFieldKey) {
        return async (value: string | null) => {
            // Pass null through unchanged. The schema stores nullable
            // strings and converting null → "" would diverge the optimistic
            // cache write from the existing `null` shape and round-trip an
            // empty string the user never typed.
            const payload = { [fieldKey]: value } as IExtractedCOIUpdate;
            await updateMutation.mutateAsync(payload);
        };
    }

    const insurerNameKey = (letter: InsurerLetter) =>
        `insurer_${letter}_name` as const;
    const insurerNaicKey = (letter: InsurerLetter) =>
        `insurer_${letter}_naic` as const;

    const populatedInsurers = INSURER_LETTERS.filter(
        (letter) => coi[insurerNameKey(letter)]
    );
    const insurersToShow = showEmpty ? INSURER_LETTERS : populatedInsurers;

    const coverages = coi.coverages.filter(isCoverage);

    function renderField(fieldKey: COIFieldKey, label: string) {
        const v =
            (coi[fieldKey as keyof IExtractedCOIResponse] as string | null) ??
            null;
        if (!v && !showEmpty) return null;
        return (
            <COIFieldRow
                label={label}
                value={v ?? ""}
                onSave={saveField(fieldKey)}
            />
        );
    }

    const certDetailsHasValue =
        !!coi.certificate_number ||
        !!coi.certificate_date ||
        !!coi.revision_number ||
        !!coi.authorized_representative;
    const producerHasValue =
        !!coi.producer_name ||
        !!coi.producer_address ||
        !!coi.producer_contact_name ||
        !!coi.producer_phone ||
        !!coi.producer_fax ||
        !!coi.producer_email;
    const insuredHasValue = !!coi.insured_name || !!coi.insured_address;
    const certHolderHasValue =
        !!coi.certificate_holder_name || !!coi.certificate_holder_address;

    return (
        <div className="space-y-4">
            {(showEmpty || certDetailsHasValue) && (
                <COISection
                    title="Certificate Details"
                    pageNumber={coi.page_num}
                    onPageClick={
                        onOpenInDocument
                            ? () =>
                                  onOpenInDocument({
                                      documentId,
                                      // No backing property — the COI is a
                                      // doc-level record, so the modal skips
                                      // its highlight-areas fetch.
                                      propertyId: null,
                                      pageNumber: coi.page_num,
                                      fieldName: "Certificate of Insurance",
                                      source: null,
                                  })
                            : undefined
                    }
                >
                    {renderField("certificate_number", "Certificate Number")}
                    {renderField("certificate_date", "Date")}
                    {renderField("revision_number", "Revision")}
                    {renderField("authorized_representative", "Authorized Rep")}
                </COISection>
            )}

            {(showEmpty || producerHasValue) && (
                <COISection title="Producer">
                    {renderField("producer_name", "Name")}
                    {renderField("producer_address", "Address")}
                    {renderField("producer_contact_name", "Contact")}
                    {renderField("producer_phone", "Phone")}
                    {renderField("producer_fax", "Fax")}
                    {renderField("producer_email", "Email")}
                </COISection>
            )}

            {(showEmpty || insuredHasValue) && (
                <COISection title="Insured">
                    {renderField("insured_name", "Name")}
                    {renderField("insured_address", "Address")}
                </COISection>
            )}

            {(showEmpty || certHolderHasValue) && (
                <COISection title="Certificate Holder">
                    {renderField("certificate_holder_name", "Name")}
                    {renderField("certificate_holder_address", "Address")}
                </COISection>
            )}

            {(showEmpty || populatedInsurers.length > 0) && (
                <COISection title="Insurers">
                    {insurersToShow.map((letter) => (
                        <InsurerRow
                            key={letter}
                            letter={letter}
                            name={coi[insurerNameKey(letter)]}
                            naic={coi[insurerNaicKey(letter)]}
                            onSaveName={saveField(insurerNameKey(letter))}
                            onSaveNaic={saveField(insurerNaicKey(letter))}
                        />
                    ))}
                </COISection>
            )}

            <CoveragesBlock coverages={coverages} />

            {(showEmpty || coi.description_of_operations) && (
                <COISection title="Description of Operations">
                    {coi.description_of_operations ? (
                        <p className="text-foreground px-3 py-2.5 text-sm leading-snug">
                            {coi.description_of_operations}
                        </p>
                    ) : (
                        <p className="text-muted-foreground/50 px-3 py-2.5 text-sm">
                            —
                        </p>
                    )}
                </COISection>
            )}
        </div>
    );
}
